import cn from 'classnames';
import React, { useRef } from 'react';
import { mergeProps, useHover, useTab } from 'react-aria';
import Tooltip from 'components/Tooltip';
import useMergedRefs from 'hooks/useMergedRefs';
import { ICON_CLASS_NAME } from './constants';
import * as S from './styles';
import { TabProps } from './types';

function ForwardedComponent<T extends object>(
  { item, state }: TabProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.ReactElement | null {
  const ariaRef = useRef<HTMLDivElement>(null);
  const mergedRefs = useMergedRefs<HTMLDivElement>([ref, ariaRef]);
  const { tabProps } = useTab(item, state, ariaRef);
  const { hoverProps, isHovered } = useHover({
    isDisabled: state.isDisabled,
  });

  const { icon, iconPosition, tooltip } = item.props ?? {};
  const effectiveIconPosition = icon && !iconPosition ? 'left' : iconPosition;

  const mergedTabProps = mergeProps(tabProps, hoverProps);

  return (
    <S.Tab {...mergedTabProps} data-hovered={isHovered} ref={mergedRefs}>
      <Tooltip contents={tooltip} isDisabled={!tooltip} arrow>
        <S.Contents
          $iconPosition={effectiveIconPosition}
          $disabled={state?.disabledKeys.has(item?.key)}
        >
          {React.isValidElement(icon) &&
            React.cloneElement(icon, {
              className: cn(ICON_CLASS_NAME, icon.props.className),
            })}

          {item.rendered}
        </S.Contents>
      </Tooltip>
    </S.Tab>
  );
}

const Tab = React.forwardRef(ForwardedComponent) as <T extends object>(
  props: TabProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement | null;

export default Tab;
